import React, { useEffect, useState } from "react";
import DayBasisSwitch from "../../CoreComponents/DayBasisSwitch";
import { DAY_COMPLETED } from "../../Functionalities/dayBasis";
import { fmtDate, rupees } from "../Insights/format";
import Stat from "../Settlements/Stat";
import "../Settlements/settlements.css";
import "./riderReport.css";
import { canSettle } from "../Settlements/access";
import RiderOrders from "./RiderOrders";
import RiderPayments from "./RiderPayments";
import RiderStatements from "./RiderStatements";
import { PAGE_SIZE, fetchRiderOrders, fetchWallet, pageLabel } from "./riderReportData";

const NO_DATES = { from: "", to: "" };

const TILES = [
  ["collected", "Collected by rider"],
  ["handedIn", "Handed in"],
  ["toGet", "Balance to get"],
  ["earned", "Earned by rider"],
  ["paid", "Paid to rider"],
  ["toPay", "Balance to pay"],
];

export default function RiderReport({ riderId }) {
  const [dates, setDates] = useState(NO_DATES);
  const [basis, setBasis] = useState(DAY_COMPLETED);
  const [page, setPage] = useState(1);
  const [wallet, setWallet] = useState(null);
  const [orders, setOrders] = useState({ count: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const range = dates.from && dates.to ? dates : null;

  useEffect(() => {
    let live = true;
    fetchWallet(riderId, range, basis).then((figures) => live && setWallet(figures));
    return () => {
      live = false;
    };
  }, [riderId, range, basis, version]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchRiderOrders(riderId, range, basis, page).then((result) => {
      if (!live) return;
      setOrders(result);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [riderId, range, basis, page]);

  const changeDates = (key) => (e) => {
    setPage(1);
    setDates((prev) => ({ ...prev, [key]: e.target.value }));
  };
  const clearDates = () => {
    setPage(1);
    setDates(NO_DATES);
  };
  const changeBasis = (value) => {
    setPage(1);
    setBasis(value);
  };
  const lastPage = Math.max(1, Math.ceil(orders.count / PAGE_SIZE));

  return (
    <section className="stl rider-report">
      <div className="stl-card stl-toolbar">
        <div className="stl-table-bar__title">
          <h2 className="stl-title">Report</h2>
          <span className="stl-subtitle">{range ? `${fmtDate(range.from)} to ${fmtDate(range.to)}` : "All time"}</span>
        </div>
        <div className="stl-toolbar__group">
          <label className="stl-field">
            <span className="stl-field__label">From</span>
            <input className="stl-input" type="date" value={dates.from} max={dates.to || undefined} onChange={changeDates("from")} />
          </label>
          <label className="stl-field">
            <span className="stl-field__label">To</span>
            <input className="stl-input" type="date" value={dates.to} min={dates.from || undefined} onChange={changeDates("to")} />
          </label>
          {(dates.from || dates.to) && (
            <button type="button" className="stl-link-btn" onClick={clearDates}>
              All time
            </button>
          )}
          <DayBasisSwitch value={basis} onChange={changeBasis} />
        </div>
      </div>

      <div className="stl-stats rider-report__tiles">
        {TILES.map(([key, label]) => (
          <Stat key={key} label={label} value={wallet ? rupees(wallet[key]) : "--"} />
        ))}
      </div>

      <div className="stl-card stl-table-card">
        <div className="stl-table-bar">
          <div className="stl-table-bar__title">
            <h2 className="stl-title">Orders</h2>
            <span className="stl-subtitle">{pageLabel(page, orders.count)}</span>
          </div>
          {lastPage > 1 && (
            <div className="stl-table-bar__tools">
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <button type="button" className="stl-btn stl-btn--sm stl-btn--secondary" disabled={page === lastPage} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
        <RiderOrders rows={orders.rows} loading={loading} />
      </div>

      {canSettle() && (
        <>
          <RiderPayments riderId={riderId} range={range} onChanged={() => setVersion((v) => v + 1)} />
          <RiderStatements riderId={riderId} range={range} version={version} />
        </>
      )}
    </section>
  );
}
